/**
 * The CAD1 pages' primitives (PLAN cad1-phase3 §6 S1; DESIGN.md beside this file). The night system is LockTools' (re-exported unchanged:
 * Page, Panel, Figure, Dl rows, CopyButton, CtaLink, Seal, the stepper and the twins); CAD1 adds one hue and a few words of grammar:
 *   - rose: the launch itself (the hero haze, the creator coin, the current step);
 *   - violet, dashed: a Preview figure, computed from the launcher code because no launcher is deployed;
 *   - amber: "High creator share" and the labels that need attention;
 *   - coral: only what cannot be undone (the tick of the launch step, "Not a CAD1 launch", Report).
 *
 * "It is not a newspaper" (Bez, 2026-10-09): a page shows titles, labels, one number and buttons. Every explanation opens from an (i) as a
 * hint (a small card: a title, a sentence or two, at most two links; a bottom card on a phone, beside the icon from md) and every longer
 * record from one quiet "Details" button as the tall sheet. Both are portals on document.body, so nothing can overflow a 360 px screen
 * and no screen grows text inline.
 */
import Image from 'next/image'
import Link from 'next/link'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import styled, { css, keyframes } from 'styled-components'
import LockStateBadge from 'views/LockTools/components/LockStateBadge'
import Seal from 'views/LockTools/components/Seal'
import { Twins } from 'views/LockTools/components/CreateParts'
import { Icon, type IconName } from 'views/TokenPages/icons'
import { COPY } from './logic/copy'

export {
  BackLink,
  CopyButton,
  CtaLink,
  DISPLAY,
  Dl,
  DlRow,
  Figure,
  FigLabel,
  FigSub,
  FigValue,
  GhostCta,
  Hairline,
  LinkOut,
  MONO,
  MonoText,
  Page,
  Panel,
  PanelTitle,
  PrimaryCta,
  SegBtn,
  Segmented,
  Skel,
  SoftCta,
  Stack,
  TONE,
  useCopy,
  useNow,
} from 'views/LockTools/ui'
export { Icon, Seal, Twins }
export type { IconName }

export const ROSE = '#3ddc84' // the CAD1 green (the green tick's colour; Bez 2026-10-10: green and gold, not pink)
export const ROSE_RGB = '61, 220, 132'
export const ROSE_TEXT = '#b8f5d0'
export const AMBER = '#f5b453'
export const CORAL = '#ff6b7a'
export const VIOLET = '#a99bff'
export const ICE = '#7fd4e8'
export const SNOW = '#f4f6ff'
export const SNOW_2 = '#c9d2e6'
export const MUTED = '#9fb4ec'
export const LINE = 'rgba(180, 200, 255, 0.18)'
export const DISPLAY_FACE = "'Gabarito', 'Gabarito Fallback', system-ui, sans-serif"
export const MONO_FACE = "'Martian Mono', 'Martian Mono Fallback', ui-monospace, monospace"
/** the md breakpoint (@pancakeswap/ui breakpoints.md): hints sit beside their (i) from here up */
const MD_PX = 852

const focus = css`
  &:focus-visible {
    outline: 2px solid ${ICE};
    outline-offset: 2px;
  }
`

/* ------------------------------------------------------------------------------------------------------------------------------- */
/* Sheets: the hint (a small card) and the tall sheet (Details, all risks)                                                           */
/* ------------------------------------------------------------------------------------------------------------------------------- */

export type HintLink = { label: string; href: string; external?: boolean; testid?: string } | { label: string; onClick: () => void; testid?: string }
export type SheetVariant = 'hint' | 'tall'

const Backdrop = styled.div<{ $variant: SheetVariant }>`
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: ${({ $variant }) => ($variant === 'hint' ? '12px 12px calc(12px + env(safe-area-inset-bottom, 0px))' : '0')};
  background: ${({ $variant }) => ($variant === 'hint' ? 'rgba(2, 3, 10, 0.5)' : 'rgba(2, 3, 10, 0.72)')};
  ${({ theme }) => theme.mediaQueries.md} {
    align-items: center;
    padding: 24px;
  }
`
const sheetText = css`
  p,
  li {
    font-size: 15px;
    line-height: 22px;
    color: ${SNOW_2};
  }
  p {
    margin: 0;
  }
`
const SheetBox = styled.div`
  position: relative;
  display: grid;
  gap: 14px;
  width: 100%;
  max-width: 640px;
  max-height: 86vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 20px 16px calc(24px + env(safe-area-inset-bottom, 0px));
  border-radius: 24px 24px 0 0;
  color: ${SNOW};
  background: linear-gradient(180deg, rgba(159, 182, 255, 0.07) 0%, transparent 30%), #0a0e22;
  border: 1px solid ${LINE};
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.12), 0 -18px 50px rgba(0, 0, 0, 0.55);
  > h2 {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 0 52px 0 0;
    font-family: ${DISPLAY_FACE};
    font-weight: 800;
    font-size: 22px;
    line-height: 28px;
  }
  ${sheetText}
  ${({ theme }) => theme.mediaQueries.md} {
    border-radius: 24px;
    padding: 24px;
  }
`
/** the hint: at most 340 px wide, a title, a sentence or two and at most two links */
const HintBox = styled.div`
  position: relative;
  display: grid;
  gap: 10px;
  width: 100%;
  max-width: 340px;
  max-height: 70vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 14px 14px 14px 16px;
  border-radius: 20px;
  color: ${SNOW};
  background: linear-gradient(180deg, rgba(${ROSE_RGB}, 0.08) 0%, transparent 40%), #0b1028;
  border: 1px solid rgba(180, 200, 255, 0.26);
  box-shadow: inset 0 1px 0 rgba(223, 233, 255, 0.14), 0 18px 44px rgba(0, 0, 0, 0.6);
  > h2 {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    margin: 0 48px 0 0;
    font-family: ${DISPLAY_FACE};
    font-weight: 800;
    font-size: 17px;
    line-height: 22px;
  }
  p,
  li {
    font-size: 14px;
    line-height: 20px;
    color: ${SNOW_2};
  }
  p {
    margin: 0;
  }
`
const SheetClose = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  cursor: pointer;
  color: ${SNOW_2};
  background: rgba(5, 7, 19, 0.6);
  border: 1px solid ${LINE};
  ${focus}
`
const HintLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  a,
  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 44px;
    padding: 0 14px;
    border-radius: 12px;
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    font-weight: 700;
    text-decoration: none;
    color: ${ICE};
    background: rgba(127, 212, 232, 0.08);
    border: 1px solid rgba(127, 212, 232, 0.32);
    &:hover {
      background: rgba(127, 212, 232, 0.16);
    }
    ${focus}
  }
`

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'

type SheetProps = {
  title: string
  onClose: () => void
  variant?: SheetVariant
  /** the (i) that opened a hint: from md the card sits beside it */
  anchor?: HTMLElement | null
  testid?: string
  links?: readonly HintLink[]
  /** a small mark after the title (the Preview pill) */
  tag?: ReactNode
  children: ReactNode
}

/** One modal card on document.body: Escape, the backdrop and the 44 px close button close it; Tab stays inside; the page does not scroll. */
export function Sheet({ title, onClose, variant = 'tall', anchor, testid, links, tag, children }: SheetProps) {
  const titleId = useId()
  const box = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<CSSProperties | undefined>()
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    box.current?.focus()
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab' || !box.current) return
      const items = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (!items.length) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const at = document.activeElement
      if (e.shiftKey && (at === first || at === box.current)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (at === last || !box.current.contains(at))) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      prev?.focus?.()
    }
  }, [onClose])
  // from md a hint sits under (or above) its (i), inside the window; on a phone it is the bottom card
  useLayoutEffect(() => {
    if (variant !== 'hint' || !anchor || !box.current || typeof window === 'undefined') return
    if (!window.matchMedia?.(`(min-width: ${MD_PX}px)`).matches) return
    const r = anchor.getBoundingClientRect()
    const w = box.current.offsetWidth
    const h = box.current.offsetHeight
    const left = Math.min(Math.max(16, r.left + r.width / 2 - w / 2), window.innerWidth - w - 16)
    let top = r.bottom + 8
    if (top + h > window.innerHeight - 16) top = Math.max(16, r.top - 8 - h)
    setPos({ position: 'fixed', left, top, margin: 0 })
  }, [variant, anchor])
  const Box = variant === 'hint' ? HintBox : SheetBox
  return createPortal(
    <Backdrop $variant={variant} onClick={onClose} data-testid="cad1-sheet-backdrop">
      <Box
        ref={box}
        style={pos}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testid ?? 'cad1-sheet'}
        data-variant={variant}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId}>
          {title}
          {tag}
        </h2>
        <SheetClose type="button" onClick={onClose} aria-label={COPY.close} data-testid="cad1-sheet-close">
          <Icon name="close" size={20} />
        </SheetClose>
        {children}
        {links?.length ? (
          <HintLinks>
            {links.map((l) =>
              'onClick' in l ? (
                <button
                  key={l.label}
                  type="button"
                  data-testid={l.testid}
                  onClick={() => {
                    onClose()
                    l.onClick()
                  }}
                >
                  {l.label}
                  <Icon name="chevronRight" size={14} />
                </button>
              ) : l.external ? (
                <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={l.testid}>
                  {l.label}
                  <Icon name="external" size={14} />
                </a>
              ) : (
                <Link key={l.label} href={l.href} data-testid={l.testid}>
                  {l.label}
                  <Icon name="chevronRight" size={14} />
                </Link>
              ),
            )}
          </HintLinks>
        ) : null}
      </Box>
    </Backdrop>,
    document.body,
  )
}

type SheetContent = { title: string; body: ReactNode; testid?: string; variant?: SheetVariant; links?: readonly HintLink[] }
const SheetCtx = createContext<(c: SheetContent | null) => void>(() => undefined)

/** Wraps a page: anything inside can open one sheet with fixed content (the full risk list from a hint's "All risks" link). */
export function SheetHost({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<SheetContent | null>(null)
  const close = useCallback(() => setContent(null), [])
  return (
    <SheetCtx.Provider value={setContent}>
      {children}
      {content && typeof document !== 'undefined' ? (
        <Sheet title={content.title} onClose={close} variant={content.variant} testid={content.testid} links={content.links}>
          {content.body}
        </Sheet>
      ) : null}
    </SheetCtx.Provider>
  )
}
export const useSheet = () => useContext(SheetCtx)

/** The "All risks" link of a hint: opens the full list of 15 risks in the tall sheet. */
export function useRisksLink(): HintLink {
  const open = useSheet()
  return useMemo(
    () => ({ label: COPY.allRisks, testid: 'cad1-risks-open', onClick: () => open({ title: COPY.allRisks, body: <RiskList items={COPY.risks} />, testid: 'cad1-risk-sheet' }) }),
    [open],
  )
}

const InfoButton = styled.button`
  display: inline-grid;
  place-items: center;
  flex: none;
  width: 44px;
  height: 44px;
  /* the 44 px target stays inside its row: it only tucks into the line height and toward the words before it */
  margin: -10px 0 -10px -6px;
  padding: 0;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  color: ${MUTED};
  background: transparent;
  &:hover {
    color: ${SNOW};
  }
  ${focus}
`
/** The (i): a 44 px target around an 18 px icon; it opens its hint (the title, the words, at most two links). */
export function Info({
  title,
  text,
  links,
  testid,
  tag,
  className,
}: {
  title: string
  text: ReactNode
  links?: readonly HintLink[]
  testid?: string
  /** a mark after the hint's title (the Preview pill on a computed figure) */
  tag?: ReactNode
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const btn = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  return (
    <>
      <InfoButton
        ref={btn}
        type="button"
        className={className}
        aria-label={`About: ${title}`}
        aria-haspopup="dialog"
        data-testid={testid}
        onClick={() => setOpen(true)}
      >
        <Icon name="info" size={18} />
      </InfoButton>
      {open ? (
        <Sheet variant="hint" title={title} onClose={close} anchor={btn.current} testid={testid ? `${testid}-sheet` : undefined} links={links} tag={tag}>
          {typeof text === 'string' ? <p>{text}</p> : text}
        </Sheet>
      ) : null}
    </>
  )
}

export const QuietSmallBtn = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  min-width: 44px;
  padding: 0 18px;
  border-radius: 14px;
  cursor: pointer;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  color: ${SNOW_2};
  background: rgba(5, 7, 19, 0.45);
  border: 1px solid ${LINE};
  &:hover {
    color: ${SNOW};
    border-color: ${ICE};
  }
  .dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${AMBER};
    box-shadow: 0 0 0 3px rgba(245, 180, 83, 0.2);
  }
  ${focus}
`
/** The one quiet 44 px "Details" button of a screen: it opens the tall sheet with the full record (its content stays live while open). */
export function DetailsButton({
  title,
  testid,
  changed,
  label = COPY.details,
  children,
  style,
}: {
  title: string
  testid: string
  /** a row that is only in the sheet changed: an amber dot on the button */
  changed?: boolean
  label?: string
  children: ReactNode
  style?: CSSProperties
}) {
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  return (
    <>
      <QuietSmallBtn type="button" aria-haspopup="dialog" data-testid={testid} data-changed={changed ? 'true' : 'false'} onClick={() => setOpen(true)} style={style}>
        {label}
        {changed ? <span className="dot" aria-hidden="true" /> : null}
        <Icon name="chevronRight" size={16} />
      </QuietSmallBtn>
      {open ? (
        <Sheet title={title} onClose={close} testid={`${testid}-sheet`}>
          {children}
        </Sheet>
      ) : null}
    </>
  )
}

/** A block of the tall sheet: a small title and its lines. */
export const SheetSection = styled.section<{ $changed?: boolean }>`
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(159, 182, 255, 0.05), rgba(5, 7, 19, 0.45));
  border: 1px solid ${({ $changed }) => ($changed ? AMBER : LINE)};
  box-shadow: ${({ $changed }) => ($changed ? `0 0 0 3px ${AMBER}22` : 'inset 0 1px 0 rgba(223, 233, 255, 0.08)')};
  h3 {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${MUTED};
  }
  .big {
    font-family: ${DISPLAY_FACE};
    font-weight: 800;
    font-size: 18px;
    line-height: 24px;
    color: ${SNOW};
    overflow-wrap: anywhere;
  }
  .mono {
    font-family: ${MONO_FACE};
    font-size: 13px;
    line-height: 20px;
    color: ${SNOW};
    overflow-wrap: anywhere;
    word-break: break-all;
  }
  .was {
    font-size: 13px;
    line-height: 18px;
    color: ${AMBER};
  }
  .acts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`

/* ------------------------------------------------------------------------------------------------------------------------------- */
/* Tags, labels                                                                                                                      */
/* ------------------------------------------------------------------------------------------------------------------------------- */

const tagLook = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${VIOLET};
  background: rgba(169, 155, 255, 0.12);
  border: 1px dashed rgba(169, 155, 255, 0.7);
  white-space: nowrap;
`
const TagButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex: none;
  min-height: 44px;
  min-width: 44px;
  margin: -9px 0;
  padding: 0;
  border: 0;
  cursor: pointer;
  background: transparent;
  ${focus}
  span {
    ${tagLook}
  }
`
/** The Preview mark inside a hint or the Details sheet (not a button: it explains nothing more than where it stands). */
export const PreviewPill = () => (
  <span style={{ display: 'inline-flex' }} data-testid="cad1-preview-pill">
    <TagSpan>
      <Icon name="spark" size={12} strokeWidth={2} />
      {COPY.previewTag}
    </TagSpan>
  </span>
)
const TagSpan = styled.span`
  ${tagLook}
  min-height: 22px;
  font-size: 12px;
  letter-spacing: 0.02em;
  text-transform: none;
`
/** "Preview": this figure is an estimate from the launcher code, not a read of a deployed launcher. Tapping it says so, in plain words. */
export function PreviewTag({ testid = 'cad1-preview-tag' }: { testid?: string }) {
  const [open, setOpen] = useState(false)
  const btn = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  return (
    <>
      <TagButton ref={btn} type="button" data-testid={testid} aria-label={`${COPY.previewTag}: ${COPY.previewInfo}`} aria-haspopup="dialog" onClick={() => setOpen(true)}>
        <span>
          <Icon name="spark" size={13} strokeWidth={2} />
          {COPY.previewTag}
        </span>
      </TagButton>
      {open ? (
        <Sheet variant="hint" title={COPY.previewTag} onClose={close} anchor={btn.current}>
          <p>{COPY.previewInfo}</p>
        </Sheet>
      ) : null}
    </>
  )
}

export const Pill = styled.span<{ $tone: 'rose' | 'amber' | 'coral' | 'ice' | 'violet' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12.5px;
  line-height: 16px;
  font-weight: 700;
  white-space: nowrap;
  ${({ $tone }) => {
    const c = { rose: ROSE_TEXT, amber: AMBER, coral: CORAL, ice: ICE, violet: VIOLET, muted: MUTED }[$tone]
    return css`
      color: ${c};
      border: 1px solid ${c}66;
      background: ${c}1f;
    `
  }}
`

/** "High creator share": amber, never blocking; after a launch it comes only from the Launched event. With `info`, its hint beside it. */
export const HighShare = ({ testid = 'cad1-high-share', info }: { testid?: string; info?: boolean }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    <Pill $tone="amber" data-testid={testid}>
      <Icon name="warning" size={14} strokeWidth={2.2} />
      {COPY.highShare}
    </Pill>
    {info ? <Info title={COPY.highShare} text={COPY.highShareInfo} testid={`${testid}-info`} /> : null}
  </span>
)

/** "not verified", with the hint that says what it means. */
export const NotVerified = ({ testid = 'cad1-not-verified' }: { testid?: string }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    <Pill $tone="muted" data-testid={testid}>
      {COPY.notVerified}
    </Pill>
    <Info title={COPY.notVerified} text={COPY.notVerifiedInfo} testid={`${testid}-info`} />
  </span>
)

type Tone = 'info' | 'warn' | 'danger' | 'preview' | 'ok'
const TONE_COLOR: Record<Tone, string> = { info: ICE, warn: AMBER, danger: CORAL, preview: VIOLET, ok: ICE }
const StatusBox = styled.div<{ $tone: Tone }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  min-height: 44px;
  min-width: 0;
  padding: 0 4px 0 12px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  color: ${SNOW};
  scroll-margin-top: 88px;
  outline: none;
  &:focus-visible {
    outline: 2px solid ${ICE};
    outline-offset: 2px;
  }
  ${({ $tone }) => css`
    background: ${TONE_COLOR[$tone]}14;
    border: 1px ${$tone === 'preview' ? 'dashed' : 'solid'} ${TONE_COLOR[$tone]}59;
    > svg {
      flex: none;
      color: ${TONE_COLOR[$tone]};
    }
  `}
  .t {
    flex: 1 1 auto;
    min-width: 0;
  }
  .act {
    display: flex;
    gap: 6px;
    margin-left: auto;
  }
`
/** A status or error label: six words or fewer, its sentence in the hint, its one action beside it. */
export function StatusLabel({
  tone = 'info',
  icon,
  children,
  info,
  infoTitle,
  links,
  action,
  testid,
  focusable,
  role = 'status',
}: {
  tone?: Tone
  icon?: IconName | 'spinner'
  children: ReactNode
  info?: ReactNode
  infoTitle?: string
  links?: readonly HintLink[]
  action?: ReactNode
  testid?: string
  /** the page may move the focus here (a reason the creator must see) */
  focusable?: boolean
  role?: string
}) {
  return (
    <StatusBox $tone={tone} data-testid={testid} role={role} tabIndex={focusable ? -1 : undefined}>
      {icon === 'spinner' ? <Spinner /> : <Icon name={icon ?? (tone === 'warn' || tone === 'danger' ? 'warning' : tone === 'ok' ? 'check' : 'info')} size={18} />}
      <span className="t">{children}</span>
      {info ? <Info title={infoTitle ?? (typeof children === 'string' ? children : COPY.details)} text={info} links={links} testid={testid ? `${testid}-info` : undefined} /> : null}
      {action ? <span className="act">{action}</span> : <span style={{ width: 8 }} />}
    </StatusBox>
  )
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`
export const Spinner = styled.span.attrs({ 'aria-hidden': true })`
  flex: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(${ROSE_RGB}, 0.25);
  border-top-color: ${ROSE};
  animation: ${spin} 0.9s linear infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/** A small button inside a label (Retry, Start fresh): 44 px tall, ice outline. */
export const SmallBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 44px;
  min-width: 44px;
  padding: 0 14px;
  border-radius: 12px;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  color: ${ICE};
  background: rgba(127, 212, 232, 0.08);
  border: 1px solid rgba(127, 212, 232, 0.36);
  &:hover {
    background: rgba(127, 212, 232, 0.16);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  ${focus}
`

/** The one primary action of a page (Start, Next, Launch): the brand gradient, 52 px (56 px with $tall). */
export const GoBtn = styled.button<{ $tall?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: ${({ $tall }) => ($tall ? 56 : 52)}px;
  padding: 0 14px;
  border: 0;
  border-radius: 16px;
  cursor: pointer;
  font-family: ${DISPLAY_FACE};
  font-weight: 800;
  font-size: 17px;
  text-decoration: none;
  color: #04130b;
  background: linear-gradient(180deg, #4be894 0%, #1f9d55 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 10px 28px rgba(31, 157, 85, 0.45);
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    box-shadow: none;
  }
  ${focus}
`
/** The quiet twin (Cancel, Back, See launches): same size, glass outline. */
export const QuietBtn = styled.button<{ $tall?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: ${({ $tall }) => ($tall ? 56 : 52)}px;
  padding: 0 14px;
  border-radius: 16px;
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  color: ${SNOW};
  background: rgba(5, 7, 19, 0.5);
  border: 1px solid ${LINE};
  &:hover {
    border-color: ${ICE};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  ${focus}
`
/** In preview (or when launches are off), the Launch slot: a disabled block of the same size that says why in its hint. */
export const ClosedSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 56px;
  padding: 0 4px 0 12px;
  border-radius: 16px;
  text-align: center;
  font-size: 15px;
  line-height: 20px;
  font-weight: 800;
  color: ${VIOLET};
  background: rgba(169, 155, 255, 0.08);
  border: 1px dashed rgba(169, 155, 255, 0.6);
`

/** Twins that stay equal from 360 px up and fill their column, so both edges line up with the rows above them at every width
 * (a 560 px cap left them 80 px short of the 640 px step column at 1440, r212). */
export const TwinRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  > * {
    min-width: 0;
  }
`

/** The screen's title: Gabarito 30/34 on a phone, 44/48 from md, with its (i) beside it. */
export const H1 = styled.h1`
  margin: 0;
  font-family: ${DISPLAY_FACE};
  font-weight: 800;
  font-size: 30px;
  line-height: 34px;
  letter-spacing: -0.02em;
  color: ${SNOW};
  overflow-wrap: anywhere;
  outline: none;
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 44px;
    line-height: 48px;
  }
`
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`
/** The one number of a step: Gabarito 40/44. */
export const BigNumber = styled.span`
  min-width: 0;
  font-family: ${DISPLAY_FACE};
  font-weight: 800;
  font-size: 40px;
  line-height: 44px;
  letter-spacing: -0.02em;
  color: ${SNOW};
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
  &[data-dim='true'] {
    color: rgba(201, 210, 230, 0.4);
  }
`
/** Section heading inside the Details sheet. */
export const H2 = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-family: ${DISPLAY_FACE};
  font-weight: 800;
  font-size: 20px;
  line-height: 26px;
  letter-spacing: -0.01em;
  color: ${SNOW};
`
export const Lead = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 22px;
  color: ${SNOW_2};
`
export const Caption = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: ${MUTED};
`
/** A field label: 13/18, bold. */
export const Label = styled.label`
  font-size: 13px;
  line-height: 18px;
  font-weight: 700;
  color: ${SNOW_2};
`

/** Fluent Emoji 3D (MIT, public/images/fluent): decorative, the words beside it carry the meaning. */
export const Fluent3d = ({ name, size, className, style }: { name: string; size: number; className?: string; style?: CSSProperties }) => (
  <Image className={className} src={`/images/fluent/${name}.png`} alt="" width={size} height={size} unoptimized draggable={false} style={{ flex: 'none', ...style }} />
)

/* ------------------------------------------------------------------------------------------------------------------------------- */
/* Addresses                                                                                                                         */
/* ------------------------------------------------------------------------------------------------------------------------------- */

const AddrText = styled.span`
  font-family: ${MONO_FACE};
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
  word-break: break-all;
  color: ${SNOW};
  b {
    font-weight: 700;
    color: ${ROSE};
  }
`
/** A token address with its …cad1 ending in rose: full (success, Details) or short (rows). Never lowercased; `title` holds it whole. */
export function Cad1Addr({ address, full, testid }: { address: string; full?: boolean; testid?: string }) {
  const a = address || ''
  const ends = /cad1$/i.test(a)
  const shown = full ? a : `${a.slice(0, 6)}…${a.slice(-4)}`
  return (
    <AddrText data-testid={testid} title={a}>
      {ends ? (
        <>
          {shown.slice(0, -4)}
          <b>{shown.slice(-4)}</b>
        </>
      ) : (
        shown
      )}
    </AddrText>
  )
}

/* ------------------------------------------------------------------------------------------------------------------------------- */
/* The coin stage: the creator's coin beside the gold CADINU Lock padlock, the 3D rocket                                             */
/* ------------------------------------------------------------------------------------------------------------------------------- */

const rise = keyframes`
  0% { transform: translate(0, 10px) rotate(-8deg); opacity: 0; }
  60% { opacity: 1; }
  100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
`
const tilt = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  100% { transform: translate(-6%, -6%) rotate(-40deg); }
`
const padOn = keyframes`
  0% { background: rgba(${ROSE_RGB}, 0.18); box-shadow: none; }
  100% { background: ${ROSE}; box-shadow: 0 0 10px rgba(${ROSE_RGB}, 0.9), 0 0 2px #fff inset; }
`
const HeroArt = styled.div<{ $size: 'hero' | 'stage' }>`
  position: relative;
  isolation: isolate;
  width: ${({ $size }) => ($size === 'hero' ? 'min(260px, 70vw)' : '128px')};
  max-width: 100%;
  aspect-ratio: ${({ $size }) => ($size === 'hero' ? '300 / 220' : '1 / 1')};
  margin: 0 auto;
  flex: none;
  ${({ theme, $size }) =>
    $size === 'stage'
      ? css`
          /* the launch step's stage is a little smaller on a phone, so its facts and twins fit one 360 x 800 screen */
          &[data-pose='ready'] {
            width: 104px;
          }
          ${theme.mediaQueries.md} {
            width: 160px;
            &[data-pose='ready'] {
              width: 160px;
            }
          }
        `
      : css`
          ${theme.mediaQueries.md} {
            width: min(300px, 100%);
          }
        `}
  /* the one glow of the screen: rose and violet behind the coin and the padlock */
  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    inset: -12% -14%;
    background: radial-gradient(48% 52% at 40% 54%, rgba(${ROSE_RGB}, 0.36) 0%, rgba(247, 207, 98, 0.16) 46%, transparent 72%);
    pointer-events: none;
  }
  .coin {
    position: absolute;
    left: ${({ $size }) => ($size === 'hero' ? '2%' : '0')};
    bottom: ${({ $size }) => ($size === 'hero' ? '6%' : '4%')};
    width: ${({ $size }) => ($size === 'hero' ? '56%' : '76%')};
    filter: drop-shadow(0 14px 22px rgba(0, 0, 0, 0.55));
  }
  .lock {
    position: absolute;
    right: 0;
    bottom: ${({ $size }) => ($size === 'hero' ? '0' : '4%')};
    width: ${({ $size }) => ($size === 'hero' ? '46%' : '46%')};
  }
  .rocket {
    position: absolute;
    left: ${({ $size }) => ($size === 'hero' ? '40%' : '52%')};
    top: 0;
    width: ${({ $size }) => ($size === 'hero' ? '24%' : '32%')} !important;
    height: auto !important;
    filter: drop-shadow(0 8px 14px rgba(0, 0, 0, 0.5));
    animation: ${rise} 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both;
  }
  &[data-pose='ready'] .rocket {
    animation: ${tilt} 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both;
  }
  .pad {
    position: absolute;
    left: 50%;
    bottom: -8px;
    display: flex;
    gap: 10px;
    transform: translateX(-50%);
  }
  .pad i {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    animation: ${padOn} 0.35s ease-out both;
  }
  .pad i:nth-child(2) {
    animation-delay: 0.12s;
  }
  .pad i:nth-child(3) {
    animation-delay: 0.24s;
  }
  @media (prefers-reduced-motion: reduce) {
    .rocket,
    &[data-pose='ready'] .rocket,
    .pad i {
      animation-duration: 0s;
      animation-delay: 0s;
    }
  }
`

/** The creator's coin: a rose-gold struck medal with the logo (when given) or the symbol in its face; `fill` draws the creator's share
 * of the supply on the rim like a fuel gauge (amber at 20 % or more). */
export function Coin({ symbol, logo, className, high, fill }: { symbol?: string; logo?: string | null; className?: string; high?: boolean; fill?: number }) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const letters = (symbol || '').slice(0, 6).toUpperCase()
  const ticks = useMemo(
    () =>
      Array.from({ length: 48 }, (_, i) => {
        const a = (i / 48) * Math.PI * 2
        return { x1: 60 + Math.cos(a) * 52, y1: 60 + Math.sin(a) * 52, x2: 60 + Math.cos(a) * 56, y2: 60 + Math.sin(a) * 56 }
      }),
    [],
  )
  const fs = letters.length <= 2 ? 34 : letters.length === 3 ? 28 : letters.length === 4 ? 23 : letters.length === 5 ? 19 : 16
  const C = 2 * Math.PI * 54
  const f = typeof fill === 'number' ? Math.min(1, Math.max(0, fill)) : null
  return (
    <svg className={className} viewBox="0 0 120 120" role="img" aria-hidden="true" focusable="false" data-fill={f === null ? undefined : f.toFixed(4)}>
      <defs>
        <linearGradient id={`rim${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff0b8" />
          <stop offset="0.45" stopColor={ROSE} />
          <stop offset="1" stopColor="#0f5a33" />
        </linearGradient>
        <radialGradient id={`face${uid}`} cx="0.42" cy="0.32" r="0.8">
          <stop offset="0" stopColor="#10301f" />
          <stop offset="0.7" stopColor="#08170f" />
          <stop offset="1" stopColor="#070a1d" />
        </radialGradient>
        <clipPath id={`clip${uid}`}>
          <circle cx="60" cy="60" r="44" />
        </clipPath>
      </defs>
      <circle cx="60" cy="60" r="59" fill={`url(#rim${uid})`} />
      <circle cx="60" cy="60" r="58" fill="none" stroke="#fff" strokeOpacity="0.35" strokeWidth="1" />
      {ticks.map((t, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke="#5b1340" strokeOpacity="0.55" strokeWidth="1.4" />
      ))}
      {f !== null ? (
        <g transform="rotate(-90 60 60)">
          <circle cx="60" cy="60" r="54" fill="none" stroke="#1a0b24" strokeOpacity="0.7" strokeWidth="5" />
          {f > 0 ? (
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke={high ? AMBER : '#f3fff8'}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={`${Math.max(2, f * C)} ${C}`}
              style={{ transition: 'stroke-dasharray 0.5s ease' }}
            />
          ) : null}
        </g>
      ) : null}
      <circle cx="60" cy="60" r="47" fill={`url(#face${uid})`} stroke={high ? AMBER : '#b8f5d0'} strokeOpacity={high ? 0.95 : 0.55} strokeWidth={high ? 3 : 1.5} />
      {logo ? (
        <image href={logo} x="16" y="16" width="88" height="88" clipPath={`url(#clip${uid})`} preserveAspectRatio="xMidYMid slice" />
      ) : letters ? (
        <text x="60" y="60" textAnchor="middle" dominantBaseline="central" fontFamily={DISPLAY_FACE} fontWeight="900" fontSize={fs} fill="#eafff2">
          {letters}
        </text>
      ) : (
        <text x="60" y="61" textAnchor="middle" dominantBaseline="central" fontFamily={DISPLAY_FACE} fontWeight="900" fontSize="40" fill="#eafff2">
          $
        </text>
      )}
      <ellipse cx="44" cy="30" rx="22" ry="9" fill="#fff" opacity="0.1" transform="rotate(-24 44 30)" />
    </svg>
  )
}

/** The hero (entry) and the coin stage (the three steps): coin + padlock + rocket. Decorative: the title says what it means.
 *  `pose="ready"`: the three pad lights switch on and the rocket tilts up, in one motion (the launch step). */
export function Cad1Hero({
  size = 'hero',
  symbol,
  logo,
  high,
  fill,
  pose,
}: {
  size?: 'hero' | 'stage'
  symbol?: string
  logo?: string | null
  high?: boolean
  fill?: number
  pose?: 'ready'
}) {
  return (
    <HeroArt $size={size} aria-hidden="true" data-testid={size === 'hero' ? 'cad1-hero-art' : 'cad1-stage'} data-pose={pose}>
      <div className="lock">
        <Seal progress={0} tone="locked" decorative animate={size === 'hero'} testid="cad1-hero-lock" />
      </div>
      <Coin className="coin" symbol={symbol} logo={logo} high={high} fill={fill} />
      <Fluent3d className="rocket" name="rocket" size={size === 'hero' ? 72 : 48} />
      {pose === 'ready' ? (
        <span className="pad" data-testid="cad1-pad">
          <i />
          <i />
          <i />
        </span>
      ) : null}
    </HeroArt>
  )
}

const progress = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`
const PadBox = styled.div`
  position: relative;
  isolation: isolate;
  display: grid;
  justify-items: center;
  gap: 14px;
  width: 160px;
  margin: 0 auto;
  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    inset: -20% -30%;
    background: radial-gradient(50% 50% at 50% 60%, rgba(${ROSE_RGB}, 0.34) 0%, rgba(247, 207, 98, 0.14) 50%, transparent 74%);
  }
  .rocket {
    transform: rotate(-40deg);
    filter: drop-shadow(0 10px 16px rgba(0, 0, 0, 0.5));
  }
  .pad {
    display: flex;
    gap: 12px;
  }
  .pad i {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${ROSE};
    box-shadow: 0 0 10px rgba(${ROSE_RGB}, 0.9);
  }
  .bar {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: rgba(${ROSE_RGB}, 0.16);
  }
  .bar::after {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 40%;
    border-radius: 3px;
    background: ${ROSE};
    animation: ${progress} 1.4s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .bar::after {
      animation: none;
      width: 100%;
      opacity: 0.6;
    }
  }
`
/** Pending: the rocket on the lit pad, and an indeterminate rose bar (a send has no honest percentage). */
export function PadStage() {
  return (
    <PadBox aria-hidden="true" data-testid="cad1-pad-stage">
      <Fluent3d className="rocket" name="rocket" size={72} />
      <span className="pad">
        <i />
        <i />
        <i />
      </span>
      <span className="bar" />
    </PadBox>
  )
}

const liftOff = keyframes`
  0% { transform: translate(0, 0) rotate(-40deg); opacity: 1; visibility: visible; }
  99% { opacity: 0; visibility: visible; }
  100% { transform: translate(30%, -260%) rotate(-40deg); opacity: 0; visibility: hidden; }
`
const growIn = keyframes`
  0% { transform: scale(0.55); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`
const stampIn = keyframes`
  0% { transform: rotate(-14deg) scale(1.6); opacity: 0; }
  100% { transform: rotate(-14deg) scale(1); opacity: 1; }
`
const SuccessBox = styled.div`
  position: relative;
  isolation: isolate;
  width: 180px;
  height: 180px;
  margin: 0 auto;
  flex: none;
  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    inset: -18%;
    background: radial-gradient(50% 50% at 50% 50%, rgba(${ROSE_RGB}, 0.4) 0%, rgba(247, 207, 98, 0.16) 52%, transparent 74%);
  }
  .coin {
    width: 180px;
    height: 180px;
    filter: drop-shadow(0 16px 26px rgba(0, 0, 0, 0.55));
    animation: ${growIn} 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.6s both;
  }
  .rocket {
    position: absolute;
    left: 38%;
    top: 30%;
    animation: ${liftOff} 1s cubic-bezier(0.5, 0, 0.75, 0) 0.1s both;
    pointer-events: none;
  }
  .stamp {
    position: absolute;
    right: -6px;
    bottom: 14px;
    padding: 2px 10px;
    border-radius: 10px;
    font-family: ${DISPLAY_FACE};
    font-weight: 900;
    font-size: 18px;
    line-height: 24px;
    letter-spacing: 0.14em;
    color: ${ROSE_TEXT};
    background: rgba(10, 14, 34, 0.85);
    border: 2px solid ${ROSE};
    box-shadow: 0 0 14px rgba(${ROSE_RGB}, 0.5);
    transform: rotate(-14deg);
    animation: ${stampIn} 0.35s cubic-bezier(0.2, 1.4, 0.4, 1) 1.2s both;
  }
  @media (prefers-reduced-motion: reduce) {
    .coin,
    .stamp {
      animation: none;
    }
    .rocket {
      display: none;
    }
  }
`
/** Success: the rocket lifts out of frame, the creator's coin grows in to 180 px with the rose "LIVE" stamp (all skipped under reduced motion). */
export function SuccessStage({ symbol, logo, high }: { symbol?: string; logo?: string | null; high?: boolean }) {
  return (
    <SuccessBox data-testid="cad1-success-stage">
      <Coin className="coin" symbol={symbol} logo={logo} high={high} />
      <Fluent3d className="rocket" name="rocket" size={64} />
      <span className="stamp" aria-hidden="true">
        {COPY.tx.liveStamp}
      </span>
    </SuccessBox>
  )
}

/* ------------------------------------------------------------------------------------------------------------------------------- */
/* One confetti burst on success (never under reduced motion)                                                                        */
/* ------------------------------------------------------------------------------------------------------------------------------- */

const fall = keyframes`
  0% { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 1; }
  100% { transform: translate3d(var(--dx), 105vh, 0) rotate(var(--rot)); opacity: 0.9; }
`
const ConfettiBox = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  overflow: hidden;
  pointer-events: none;
  i {
    position: absolute;
    top: 0;
    width: 8px;
    height: 14px;
    border-radius: 2px;
    animation: ${fall} var(--dur) cubic-bezier(0.2, 0.6, 0.4, 1) var(--delay) both;
  }
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`
const CONFETTI_COLORS = [ROSE, '#f7cf62', ICE, VIOLET, '#ffffff']
export function Confetti({ testid = 'cad1-confetti' }: { testid?: string }) {
  const [on, setOn] = useState(true)
  const reduced = useMemo(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches, [])
  useEffect(() => {
    const t = setTimeout(() => setOn(false), 3200)
    return () => clearTimeout(t)
  }, [])
  const bits = useMemo(
    () =>
      Array.from({ length: 56 }, (_, i) => ({
        left: `${(i * 37) % 100}%`,
        dx: `${((i * 53) % 40) - 20}vw`,
        rot: `${(i * 97) % 720}deg`,
        dur: `${1.8 + ((i * 13) % 10) / 10}s`,
        delay: `${((i * 7) % 10) / 25}s`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      })),
    [],
  )
  if (!on || reduced) return null
  return (
    <ConfettiBox aria-hidden="true" data-testid={testid}>
      {bits.map((b, i) => (
        <i
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          style={{ left: b.left, background: b.color, ['--dx' as any]: b.dx, ['--rot' as any]: b.rot, ['--dur' as any]: b.dur, ['--delay' as any]: b.delay }}
        />
      ))}
    </ConfettiBox>
  )
}

/* ------------------------------------------------------------------------------------------------------------------------------- */
/* The launch step's facts: a 3D icon, a label, one value, the (i) in one aligned column                                            */
/* ------------------------------------------------------------------------------------------------------------------------------- */

export const FactRow = styled.div<{ $changed?: boolean }>`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 44px;
  gap: 12px;
  align-items: center;
  min-height: 60px;
  padding: 6px 6px 6px 12px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(159, 182, 255, 0.08) 0%, rgba(159, 182, 255, 0) 60%), #0b1028;
  border: 1px solid ${({ $changed }) => ($changed ? AMBER : LINE)};
  box-shadow: ${({ $changed }) => ($changed ? `0 0 0 3px ${AMBER}22` : 'inset 0 1px 0 rgba(223, 233, 255, 0.12), 0 10px 22px rgba(0, 0, 0, 0.35)')};
  .text {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  .label {
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    color: ${MUTED};
  }
  .value {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
    font-family: ${DISPLAY_FACE};
    font-weight: 800;
    font-size: 22px;
    line-height: 26px;
    color: ${SNOW};
    overflow-wrap: anywhere;
  }
  .suffix {
    font-family: inherit;
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    color: ${MUTED};
  }
  .was {
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    color: ${AMBER};
  }
  .i {
    display: grid;
    place-items: center;
  }
`

/* ------------------------------------------------------------------------------------------------------------------------------- */
/* Lists used in the Details sheets                                                                                                  */
/* ------------------------------------------------------------------------------------------------------------------------------- */

const RiskOl = styled.ol`
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: risk;
  li {
    position: relative;
    padding-left: 34px;
    font-size: 15px;
    line-height: 22px;
    color: ${SNOW_2};
    counter-increment: risk;
  }
  li::before {
    content: counter(risk);
    position: absolute;
    left: 0;
    top: 0;
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-family: ${MONO_FACE};
    font-size: 12px;
    color: ${AMBER};
    background: rgba(245, 180, 83, 0.12);
    border: 1px solid rgba(245, 180, 83, 0.4);
  }
`
export function RiskList({ items, testid }: { items: readonly string[]; testid?: string }) {
  return (
    <RiskOl data-testid={testid}>
      {items.map((r) => (
        <li key={r}>{r}</li>
      ))}
    </RiskOl>
  )
}

const RailOl = styled.ol<{ $done?: boolean }>`
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  grid-template-columns: minmax(0, 1fr);
  li {
    position: relative;
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    min-height: 48px;
    font-size: 15px;
    line-height: 20px;
    font-weight: 700;
    color: ${SNOW};
  }
  /* the rail: one line through every step */
  li + li::before {
    content: '';
    position: absolute;
    left: 17px;
    top: -14px;
    width: 2px;
    height: 20px;
    border-radius: 2px;
    background: ${({ $done }) => ($done ? ICE : `rgba(${ROSE_RGB}, 0.45)`)};
  }
  .dot {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    font-family: ${MONO_FACE};
    font-size: 14px;
    color: ${({ $done }) => ($done ? '#050713' : ROSE_TEXT)};
    background: ${({ $done }) => ($done ? ICE : 'rgba(10, 14, 34, 1)')};
    border: 2px solid ${({ $done }) => ($done ? ICE : `rgba(${ROSE_RGB}, 0.6)`)};
  }
`
/** The six parts of the one transaction, on one rail. `done` ticks all of them at once (confirmed), never one by one. */
export function Rail({ steps, done, testid }: { steps: readonly string[]; done?: boolean; testid?: string }) {
  return (
    <RailOl $done={done} data-testid={testid} data-done={done ? 'true' : 'false'}>
      {steps.map((s, i) => (
        <li key={s}>
          <span className="dot">{done ? <Icon name="check" size={18} strokeWidth={2.6} /> : i + 1}</span>
          <span>{s}</span>
        </li>
      ))}
    </RailOl>
  )
}

/* ------------------------------------------------------------------------------------------------------------------------------- */
/* The lock badge of a launch                                                                                                        */
/* ------------------------------------------------------------------------------------------------------------------------------- */

/** Has the unlock date of a launch's Launched event passed? (null before the clock is read: not yet) */
export const unlockPassed = (unlockAt: bigint, now: number | null): boolean => now !== null && BigInt(now) >= unlockAt

/**
 * The shared lock badge for a CAD1 launch (rows, the band, success). These pages read the Launched event, never the lock itself, so the
 * badge says only what the event and the clock say: "Locked until …" before the event's unlock date (the lock cannot be opened or shortened
 * before it), and past it the neutral badge with "Unlock date passed": no padlock and no "Ready to unlock", since the lock may already be
 * withdrawn or extended (audit L 2026-10-09). The certificate the badge links to reads the lock live.
 */
export function LaunchLockBadge({ unlockAt, now, label, testid }: { unlockAt: bigint; now: number | null; label: string; testid: string }) {
  return unlockPassed(unlockAt, now) ? (
    <LockStateBadge state="unavailable" label={COPY.list.lockPassed} check={false} dark testid={testid} />
  ) : (
    <LockStateBadge state="locked" label={label} check={false} dark testid={testid} />
  )
}

/* ------------------------------------------------------------------------------------------------------------------------------- */
/* The step bar: Name · Buy · Launch                                                                                                 */
/* ------------------------------------------------------------------------------------------------------------------------------- */

const StepOl = styled.ol<{ $n: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $n }) => $n}, minmax(0, 1fr));
  /* a step change scrolls the bar into view below the sticky header */
  scroll-margin-top: 88px;
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    position: relative;
    display: grid;
    justify-items: center;
    gap: 6px;
    min-width: 0;
    text-align: center;
    font-size: 13px;
    line-height: 18px;
    font-weight: 700;
    color: #aab6dc;
  }
  li + li::before {
    content: '';
    position: absolute;
    top: 17px;
    right: calc(50% + 22px);
    width: calc(100% - 44px);
    height: 2px;
    border-radius: 2px;
    background: rgba(180, 200, 255, 0.2);
  }
  li[data-done='true'] + li::before {
    background: ${ROSE};
  }
  .dot {
    position: relative;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    font-family: ${MONO_FACE};
    font-size: 14px;
    color: ${SNOW_2};
    background: #0a0e22;
    border: 2px solid rgba(180, 200, 255, 0.28);
  }
  li[data-done='true'] .dot {
    color: ${ROSE_TEXT};
    border-color: ${ROSE};
    background: rgba(${ROSE_RGB}, 0.12);
  }
  li[aria-current='step'] {
    color: ${SNOW};
  }
  li[aria-current='step'] .dot {
    color: #050713;
    background: ${ROSE};
    border-color: ${ROSE};
    box-shadow: 0 0 0 5px rgba(${ROSE_RGB}, 0.16);
  }
`
export function StepBar({ labels, step }: { labels: readonly string[]; step: number }) {
  return (
    <StepOl $n={labels.length} aria-label="Steps" data-testid="cad1-steps" data-step={step}>
      {labels.map((s, i) => (
        <li key={s} aria-current={i === step ? 'step' : undefined} data-done={i < step ? 'true' : 'false'}>
          <span className="dot">{i < step ? <Icon name="check" size={18} strokeWidth={2.6} /> : i + 1}</span>
          <span>{s}</span>
        </li>
      ))}
    </StepOl>
  )
}
